import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckIcon from '@mui/icons-material/Check';
import FormProvider from '../form/FormProvider';
import TextField from '../form/TextField';
import PhoneField from '../form/PhoneField';
import { Roles } from '../../constants';

const buildSchema = (nmlsOptional) => z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  emailAddress: z.string().email('Invalid email'),
  nmls: nmlsOptional ? z.string().optional().default('') : z.string().min(1, 'NMLS is required for approval'),
  phone: z.string().optional().default(''),
});

/**
 * Modal for Lender Admin to review/edit and approve a pending user.
 */
const ApproveUserModal = ({ user, onApprove, onClose }) => {
  const nmlsOptional = user.role === Roles.Processor || user.role === Roles.BrokerFullAccessReadOnly;
  const methods = useForm({
    resolver: zodResolver(buildSchema(nmlsOptional)),
    defaultValues: {
      firstName: user.firstName || '', lastName: user.lastName || '',
      emailAddress: user.emailAddress || '', nmls: user.nmls || '', phone: user.phone || '',
    },
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (data) => {
    setError('');
    setSubmitting(true);
    try {
      await onApprove({ userId: user.userId, ...data });
      onClose();
    } catch (err) {
      if (err?.status === 409 && err?.data?.existingFileContactId) { onClose(); throw err; }
      const msg = err.data?.title
        || err.data?.[0]?.description
        || (Array.isArray(err.data) ? err.data.map(e => e.description).join(', ') : null)
        || 'Failed to approve user';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Approve User</DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
            Review and edit the user's details. Once approved, the user will receive a confirmation email to set up their account.
          </Alert>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6 }}><TextField name="firstName" label="First Name" required size="small" /></Grid>
            <Grid size={{ xs: 6 }}><TextField name="lastName" label="Last Name" required size="small" /></Grid>
          </Grid>
          <TextField name="emailAddress" label="Email" type="email" required size="small" sx={{ mb: 2 }} />
          <TextField name="nmls" label="NMLS" size="small" required={!nmlsOptional} helperText={nmlsOptional ? 'NMLS is optional for this role' : undefined} sx={{ mb: 2 }} />
          <PhoneField name="phone" label="Phone" size="small" />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" variant="contained" color="success" disabled={submitting} startIcon={submitting ? <CircularProgress size={16} /> : <CheckIcon />}>
            {submitting ? 'Approving…' : 'Approve User'}
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};

export default ApproveUserModal;
